import { useEffect, useRef, useState } from 'react'
import ProgressPill from 'components/ProgressPill/ProgressPill'
import ClassRooms, { ClassRoom } from 'services/modals/ClassRooms'
import ClassroomDropdownOverlay from './ClassroomDropdownOverlay'
import StudentProfileDropdownMenu from './StudentProfileDropdownMenu'
import {
  HeaderContainer,
  HeaderSurface,
  SectionLabel,
  ClassroomCard,
  ClassroomName,
  ObjectivesSection,
  SummativesSection,
  SummativeLevels,
  RightSection,
  UserBadge,
  ProfileButton,
  ClassroomSection,
  SkeletonBlock,
  SkeletonCircle,
  SummativeLevelText
} from './styles'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  selectCurrentClassroom,
  setCurrentClassroom
} from 'app/reducer/studentSlice'
import {
  logout,
  selectUser,
  setUser,
  setUserAvatarColors
} from 'app/reducer/authSlice'
import { selectThemeMode, setThemeMode } from 'app/reducer/themeSlice'
import { setStoredThemeMode, ThemeMode } from 'config/theme'
import cookie from 'js-cookie'
import {
  StudentAvatarColorOption,
  useGetStudentAvatarColorsQuery,
  useGetStudentMenuQuery,
  useUpdateStudentAvatarColorsMutation
} from 'services/studentApi'
import { AppCookies } from 'util/constants'
import { useNavigate } from 'react-router-dom'
import ChevronIcon from 'components/ChevronIcon/ChevronIcon'

interface AvailableColorCode {
  id?: number
  code: string
  textColor?: string
}

const DEFAULT_AVATAR_TEXT_COLOR = '#FFFFFF'

export const getAvatarTextColor = (backgroundColor?: string) => {
  /**
   * The avatar update endpoint needs both background and text colors.
   * Derive a readable text color from the selected swatch so the badge stays
   * legible even when the menu payload has not been refreshed yet.
   */
  if (!backgroundColor) {
    return DEFAULT_AVATAR_TEXT_COLOR
  }

  const normalizedColor = backgroundColor.replace('#', '')
  const isShortHex = normalizedColor.length === 3
  const hexColor = isShortHex
    ? normalizedColor
        .split('')
        .map((colorValue) => `${colorValue}${colorValue}`)
        .join('')
    : normalizedColor

  if (hexColor.length !== 6) {
    return DEFAULT_AVATAR_TEXT_COLOR
  }

  const red = Number.parseInt(hexColor.slice(0, 2), 16)
  const green = Number.parseInt(hexColor.slice(2, 4), 16)
  const blue = Number.parseInt(hexColor.slice(4, 6), 16)

  if (Number.isNaN(red) || Number.isNaN(green) || Number.isNaN(blue)) {
    return DEFAULT_AVATAR_TEXT_COLOR
  }

  const brightness = (red * 299 + green * 587 + blue * 114) / 1000

  return brightness >= 160 ? '#000000' : '#FFFFFF'
}

interface ClassroomHeaderBarProps {
  classrooms: ClassRooms
  isLoading: boolean
  studentInitials?: string
  autoOpenSelectOnLoad?: boolean
  onClassroomChange: (classroomId: number) => void
}

const ClassroomHeaderBar = ({
  classrooms,
  isLoading,
  studentInitials,
  autoOpenSelectOnLoad = false,
  onClassroomChange
}: ClassroomHeaderBarProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const user = useAppSelector(selectUser)
  const themeMode = useAppSelector(selectThemeMode)
  const currentClassroom = useAppSelector(selectCurrentClassroom)
  const profileDropdownRef = useRef<HTMLDivElement | null>(null)
  const { data: studentMenu } = useGetStudentMenuQuery(undefined, {
    skip: !user
  })
  const { data: studentAvatarColors = [] } = useGetStudentAvatarColorsQuery(
    undefined,
    {
      skip: !user
    }
  )
  const [updateStudentAvatarColors] = useUpdateStudentAvatarColorsMutation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  /**
   * The menu response owns the initial avatar colors; this flag lets a
   * deliberate swatch selection take precedence until the next page load.
   */
  const [hasSelectedProfileColor, setHasSelectedProfileColor] = useState(false)
  /**
   * Restore the classroom that launched the assessment when the dashboard
   * remounts so every assessment exit path returns to that same classroom.
   */
  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(
    currentClassroom?.id || null
  )
  const [hasAutoOpenedSelect, setHasAutoOpenedSelect] = useState(false)
  const hasClassrooms = classrooms.length > 0
  /**
   * The classroom picker is only actionable when there is another classroom
   * to choose, while the current classroom remains visible for single-class
   * students.
   */
  const hasMultipleClassrooms = classrooms.length > 1
  const isDarkMode = themeMode === 'dark'

  const selectedColorId =
    user?.selected_color?.color_code_id ||
    user?.selected_color?.fk_color_code_id
  /**
   * The student dropdown should render the exact avatar colors returned by the
   * dedicated color-options endpoint, while keeping the existing swatch shape
   * that the profile menu already expects.
   */
  const profileSwatches = studentAvatarColors.map(
    (colorOption: StudentAvatarColorOption, index: number) => {
      /**
       * The avatar-color API returns color pairs without ids, so use a stable
       * frontend position only for the existing swatch selection contract.
       */
      return {
        id: index + 1,
        code: colorOption.avatar_background_color,
        textColor: colorOption.avatar_text_color
      }
    }
  )
  /**
   * Resolve the saved relation against the freshly loaded swatches as a
   * fallback when the menu endpoint returns an id without a background value.
   */
  const selectedProfileSwatch = profileSwatches.find(
    (colorCode) => colorCode.id === selectedColorId
  )
  const authSelectedColorCode = (user?.available_color_codes || []).find(
    (colorCode) => colorCode.id === selectedColorId
  )?.code
  const selectedColorCode =
    (hasSelectedProfileColor
      ? user?.avatar_background_color
      : studentMenu?.avatar_background_color) ||
    user?.avatar_background_color ||
    selectedProfileSwatch?.code ||
    authSelectedColorCode ||
    cookie.get(AppCookies.profile_color_code)
  const selectedAvatarTextColor =
    (hasSelectedProfileColor
      ? user?.avatar_text_color
      : studentMenu?.avatar_text_color) ||
    user?.avatar_text_color ||
    getAvatarTextColor(selectedColorCode)

  useEffect(() => {
    /**
     * Keep the menu payload on the shared auth user so the badge and future
     * consumers read one typed source of truth for avatar colors.
     */
    if (
      hasSelectedProfileColor ||
      !studentMenu?.avatar_background_color ||
      !user
    ) {
      return
    }

    /**
     * Preserve both avatar colors exactly as returned by the student menu API
     * so the backend remains the source of truth for the default badge.
     */
    const nextAvatarTextColor = studentMenu.avatar_text_color
    if (
      user.first_name === studentMenu.first_name &&
      user.last_name === studentMenu.last_name &&
      user.avatar_background_color === studentMenu.avatar_background_color &&
      user.avatar_text_color === nextAvatarTextColor
    ) {
      return
    }

    dispatch(
      setUser({
        user: {
          ...user,
          first_name: studentMenu.first_name || user.first_name,
          last_name: studentMenu.last_name || user.last_name
        }
      })
    )
    dispatch(
      setUserAvatarColors({
        avatar_background_color: studentMenu.avatar_background_color,
        avatar_text_color: nextAvatarTextColor
      })
    )
    cookie.set(
      AppCookies.profile_color_code,
      studentMenu.avatar_background_color
    )
  }, [
    dispatch,
    hasSelectedProfileColor,
    user,
    studentMenu?.first_name,
    studentMenu?.last_name,
    studentMenu?.avatar_background_color,
    studentMenu?.avatar_text_color
  ])

  /**
   * Keep the restored classroom while its fresh classroom list is loading,
   * then validate that the student still belongs to that classroom.
   */
  useEffect(() => {
    if (!classrooms.length) {
      if (isLoading) return

      setSelectedClassroomId(null)
      return
    }

    if (classrooms.length === 1) {
      setSelectedClassroomId(classrooms[0].id)
      dispatch(setCurrentClassroom(classrooms[0]))
      return
    }

    if (
      selectedClassroomId !== null &&
      classrooms.some((classroom) => classroom.id === selectedClassroomId)
    ) {
      return
    }

    setSelectedClassroomId(null)
  }, [classrooms, selectedClassroomId, dispatch, isLoading])

  useEffect(() => {
    if (!selectedClassroomId) {
      return
    }

    onClassroomChange(selectedClassroomId)
  }, [onClassroomChange, selectedClassroomId])

  useEffect(() => {
    if (!isDropdownOpen) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isDropdownOpen])

  useEffect(() => {
    if (!isProfileDropdownOpen) {
      return
    }

    /**
     * The classroom header profile menu should behave like a normal anchored
     * dropdown: escape closes it, and outside clicks should dismiss it without
     * interfering with the classroom selector state.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsProfileDropdownOpen(false)
      }
    }

    const handleWindowClick = (event: MouseEvent) => {
      if (profileDropdownRef.current?.contains(event.target as Node)) {
        return
      }

      setIsProfileDropdownOpen(false)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('click', handleWindowClick)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('click', handleWindowClick)
    }
  }, [isProfileDropdownOpen])

  useEffect(() => {
    if (!hasClassrooms && isDropdownOpen) {
      setIsDropdownOpen(false)
    }
  }, [hasClassrooms, isDropdownOpen])

  useEffect(() => {
    if (
      !autoOpenSelectOnLoad ||
      hasAutoOpenedSelect ||
      isLoading ||
      selectedClassroomId ||
      classrooms.length <= 1
    ) {
      return
    }

    setIsDropdownOpen(true)
    setHasAutoOpenedSelect(true)
  }, [
    autoOpenSelectOnLoad,
    classrooms.length,
    hasAutoOpenedSelect,
    isLoading,
    selectedClassroomId
  ])

  const selectedClassroom = classrooms.find(
    (classroom) => classroom.id === selectedClassroomId
  )

  const objectivesCompleted = selectedClassroom?.objectives?.assessed || 0
  const objectivesTotal = selectedClassroom?.objectives?.total || 0
  const objectiveFill = Math.round(
    objectivesTotal > 0 ? (objectivesCompleted / objectivesTotal) * 100 : 0
  )

  const getSummativeText = (classroom?: ClassRoom) => {
    if (!classroom?.summatives) {
      return '--'
    }

    const hasSummativeA1Key =
      classroom?.summatives?.hasOwnProperty('assessed_A1')

    const hasSummativeBKey = classroom?.summatives?.hasOwnProperty('assessed_B')

    const hasSummativeA2Key =
      classroom?.summatives?.hasOwnProperty('assessed_A2')

    return (
      <>
        {hasSummativeA1Key && (
          <SummativeLevelText
            $isAssessed={
              classroom?.summatives?.has_summative_a1 ||
              classroom?.summatives?.assessed_A1 ||
              false
            }
          >
            A1
          </SummativeLevelText>
        )}
        {hasSummativeBKey && (
          <SummativeLevelText
            $isAssessed={
              classroom?.summatives?.has_summative_b ||
              classroom?.summatives?.assessed_B ||
              false
            }
          >
            B
          </SummativeLevelText>
        )}
        {hasSummativeA2Key && (
          <SummativeLevelText
            $isAssessed={
              classroom?.summatives?.has_summative_a2 ||
              classroom?.summatives?.assessed_A2 ||
              false
            }
          >
            A2
          </SummativeLevelText>
        )}
      </>
    )

    // return values.length ? values.join(" ") : "--";
  }

  const shouldShowSkeleton = isLoading && !classrooms.length

  const onSelectClassroom = (classroomId: number) => {
    setSelectedClassroomId(classroomId)
    setIsDropdownOpen(false)
  }

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen((previousValue) => !previousValue)
  }

  const handleThemeModeChange = () => {
    /**
     * Dark mode needs to update both redux and persisted storage so the
     * classroom dashboard reflects the new mode immediately and on reload.
     */
    const nextThemeMode: ThemeMode = isDarkMode ? 'light' : 'dark'

    dispatch(setThemeMode(nextThemeMode))
    setStoredThemeMode(nextThemeMode)
  }

  const handleProfileColorChange = async (colorCode: AvailableColorCode) => {
    /**
     * Keep the badge responsive by updating the shared auth user first, then
     * persist the same background/text pair through the student avatar API.
     */
    const nextTextColor =
      colorCode.textColor || getAvatarTextColor(colorCode.code)

    setHasSelectedProfileColor(true)
    cookie.set(AppCookies.profile_color_code, colorCode.code)
    if (!user) return

    dispatch(
      setUser({
        user: {
          ...user,
          avatar_background_color: colorCode.code,
          avatar_text_color: nextTextColor,
          selected_color: {
            ...user?.selected_color,
            color_code_id: colorCode.id,
            fk_color_code_id: colorCode.id
          }
        }
      })
    )

    try {
      await updateStudentAvatarColors({
        avatar_background_color: colorCode.code,
        avatar_text_color: nextTextColor
      }).unwrap()
    } catch (error) {
      console.error('Failed to update student avatar colors', error)
    }
  }

  const handleSignOut = async () => {
    /**
     * Close the anchored menu before logging out so the dashboard does not
     * briefly re-render the dropdown while auth state is being cleared.
     */
    setIsProfileDropdownOpen(false)
    await dispatch(logout())
    navigate('/')
  }

  return (
    <HeaderContainer>
      <HeaderSurface>
        <ClassroomSection>
          <SectionLabel style={{ marginLeft: 24 }}>Classroom</SectionLabel>
          {shouldShowSkeleton ? (
            <ClassroomCard>
              <SkeletonBlock width='190px' height='14px' radius='10px' />
              <SkeletonBlock width='13px' height='9px' radius='4px' />
            </ClassroomCard>
          ) : (
            <ClassroomCard
              $isDisabled={!hasMultipleClassrooms}
              role='button'
              aria-disabled={!hasMultipleClassrooms}
              tabIndex={hasMultipleClassrooms ? 0 : -1}
              onClick={() => {
                if (!hasMultipleClassrooms) {
                  return
                }

                setIsDropdownOpen((prev) => !prev)
              }}
              onKeyDown={(event) => {
                if (!hasMultipleClassrooms) {
                  return
                }

                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setIsDropdownOpen((prev) => !prev)
                }
              }}
            >
              <ClassroomName>
                {selectedClassroom?.name ||
                  (hasClassrooms ? 'Select classroom' : 'No classrooms')}
              </ClassroomName>
              <ChevronIcon
                size='sm'
                direction='down'
                disabled={!hasMultipleClassrooms}
                disableHover={!hasMultipleClassrooms}
              />
            </ClassroomCard>
          )}
        </ClassroomSection>

        <ObjectivesSection>
          <SectionLabel>Objectives</SectionLabel>
          {shouldShowSkeleton ? (
            <>
              <SkeletonBlock width='84px' height='16px' radius='12px' />
              <SkeletonBlock width='48px' height='12px' radius='8px' />
            </>
          ) : (
            <ProgressPill
              fill={objectiveFill}
              percentage={objectiveFill}
              bottomLabel={`${objectivesCompleted} / ${objectivesTotal}`}
            />
          )}
        </ObjectivesSection>

        <SummativesSection>
          <SectionLabel>Benchmarks</SectionLabel>
          {shouldShowSkeleton ? (
            <SkeletonBlock width='70px' height='20px' radius='8px' />
          ) : (
            <SummativeLevels>
              {getSummativeText(selectedClassroom)}
            </SummativeLevels>
          )}
        </SummativesSection>

        <RightSection ref={profileDropdownRef}>
          {shouldShowSkeleton ? (
            <SkeletonCircle width='40px' height='40px' />
          ) : (
            <ProfileButton
              type='button'
              onClick={toggleProfileDropdown}
              aria-expanded={isProfileDropdownOpen}
              aria-haspopup='menu'
            >
              <UserBadge
                $backgroundColor={selectedColorCode}
                $textColor={selectedAvatarTextColor}
              >
                {studentInitials}
              </UserBadge>
              <ChevronIcon direction='down' size='sm' ariaLabel='Profile' />
            </ProfileButton>
          )}
          {shouldShowSkeleton && (
            <SkeletonBlock width='16px' height='11px' radius='4px' />
          )}
          {isProfileDropdownOpen && (
            <StudentProfileDropdownMenu
              isDarkMode={isDarkMode}
              profileSwatches={profileSwatches}
              selectedColorCode={selectedColorCode}
              selectedColorId={selectedColorId}
              onThemeModeChange={handleThemeModeChange}
              onProfileColorChange={handleProfileColorChange}
              onSignOut={handleSignOut}
            />
          )}
        </RightSection>
      </HeaderSurface>

      {isDropdownOpen && hasMultipleClassrooms && (
        <ClassroomDropdownOverlay
          classrooms={classrooms}
          isLoading={isLoading}
          selectedClassroomId={selectedClassroomId}
          onClose={() => setIsDropdownOpen(false)}
          onSelectClassroom={onSelectClassroom}
          getSummativeText={getSummativeText}
        />
      )}
    </HeaderContainer>
  )
}

export default ClassroomHeaderBar
